import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@workspace/ui/components/dialog'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { CircleCheck, Clock, Plus, X } from 'lucide-react'
import type { ReactNode } from 'react'
import { areaLabel, copy, findingLabel } from '../../copy/investor-copy'
import { formatShortDate } from '../following/dates'
import { savedByLine } from './assessment-card'
import { areaRows, otherAreaRows } from './assessment-rows'
import { QuoteBlock } from './quote-block'
import { PrivateTag } from './rel-card'
import { SourceTag } from './source-tag'
import type { Relationship } from './types'
import { VerdictPill } from './verdict-pill'

const LABEL = 'text-xs font-medium uppercase tracking-wider text-muted-foreground'

const BASES = ['on_record', 'outside_record', 'own_calculation', 'mixed', 'unstated'] as const
type Basis = (typeof BASES)[number]
const isBasis = (value: string): value is Basis => (BASES as readonly string[]).includes(value)

const FOLLOWING_STATES: Relationship['state'][] = [
  'ACTIVE',
  'CONDITION_MET',
  'WAITING_CHECK',
  'WAITING_PLACE',
]

function Decision({ data }: { data: Relationship }) {
  const { follow, decision, state } = data
  if (follow && FOLLOWING_STATES.includes(state)) {
    return (
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-sm font-semibold text-foreground">{copy('win.following')}</span>
        <span className="text-sm text-muted-foreground">
          {copy('win.followingSince', { date: formatShortDate(follow.followedAt) })}
        </span>
        {state === 'CONDITION_MET' && (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-400">
            <CircleCheck className="size-3.5" />
            {copy('win.conditionMet')}
          </span>
        )}
      </div>
    )
  }
  if (decision) {
    return (
      <div className="space-y-1">
        <p className="text-sm font-semibold text-foreground">
          {copy('win.passed', {
            date: formatShortDate(decision.createdAt),
            reason: decision.reason ?? copy('pass.outcome.none'),
          })}
        </p>
        {decision.revisitWhen && (
          <p className="text-sm text-foreground/80">
            {copy('win.revisit', { revisit: decision.revisitWhen })}
          </p>
        )}
      </div>
    )
  }
  return <span className="text-sm text-muted-foreground">{copy('win.noDecision')}</span>
}

function Block({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className={LABEL}>{label}</p>
      {children}
    </div>
  )
}

/** The whole read of one startup, as the investor's AI saved it. Every part is optional. */
export function AssessmentWindow({
  data,
  open,
  onOpenChange,
  onFollow,
  onViewSource,
}: {
  data: Relationship
  open: boolean
  onOpenChange: (open: boolean) => void
  onFollow: (suggested?: string | null) => void
  onViewSource?: (claimId: string) => void
}) {
  const assessment = data.assessment
  if (!assessment) return null
  const startup = data.startup.name
  const title = copy('win.title', { startup })
  const rows = areaRows(assessment.dimensions)
  const others = otherAreaRows(assessment.otherDimensions)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        overlayClassName="bg-black/60"
        aria-label={title}
        className="bottom-0 top-auto left-0 max-h-[94dvh] max-w-full translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-b-none rounded-t-2xl bg-input p-0 pb-[max(12px,env(safe-area-inset-bottom))] sm:top-[50%] sm:bottom-auto sm:left-[50%] sm:max-h-[calc(100dvh-48px)] sm:max-w-[40rem] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-2xl sm:pb-0 flex"
      >
        <header className="shrink-0 border-b border-border px-4 pb-4 pt-5 sm:px-6">
          <div className="flex items-start justify-between gap-3">
            <DialogTitle className="min-w-0 text-xl font-semibold leading-7 text-foreground">
              {title}
            </DialogTitle>
            <DialogClose
              aria-label={copy('win.close')}
              className="flex size-10 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-white/[0.04] hover:text-foreground"
            >
              <X className="size-4" />
            </DialogClose>
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="text-xs text-muted-foreground">{savedByLine(assessment)}</span>
            <PrivateTag label={copy('ai.private')} />
          </div>
          {assessment.movedAgainstAt && (
            <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
              <Clock className="size-3.5" />
              {copy('ai.moved', { startup, date: formatShortDate(assessment.movedAgainstAt) })}
            </p>
          )}
          <DialogDescription className="sr-only">{savedByLine(assessment)}</DialogDescription>
        </header>

        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-4 py-6 [overflow-wrap:anywhere] sm:px-6">
          <section className="space-y-4 rounded-xl border border-border/40 bg-card/30 px-5 py-4">
            <Block label={copy('win.decision')}>
              <div className="mt-1.5">
                <Decision data={data} />
              </div>
            </Block>
            {(assessment.mainRisk || assessment.mainRiskText) && (
              <Block label={copy('ai.mainRisk')}>
                {assessment.mainRisk && (
                  <p className="mt-1.5 text-sm font-semibold text-foreground">
                    {areaLabel(assessment.mainRisk)}
                  </p>
                )}
                {assessment.mainRiskText && (
                  <p className="mt-0.5 text-sm leading-relaxed text-foreground/90">
                    {assessment.mainRiskText}
                  </p>
                )}
              </Block>
            )}
            {assessment.wouldChangeMind && (
              <Block label={copy('ai.wouldChange')}>
                <p className="mt-1.5 text-sm leading-relaxed text-foreground/90">
                  {assessment.wouldChangeMind}
                </p>
              </Block>
            )}
            {assessment.ringOn && data.state === 'NONE' ? (
              <div className="flex items-center justify-between gap-3 border-t border-border pt-4">
                <p className="min-w-0 text-sm text-foreground/90">
                  {copy('ai.followRow', { startup, ringOn: assessment.ringOn })}
                </p>
                <SecondaryButton
                  size="sm"
                  icon={<Plus />}
                  onClick={() => onFollow(assessment.ringOn)}
                >
                  {copy('ai.follow')}
                </SecondaryButton>
              </div>
            ) : (
              assessment.ringOn && (
                <Block label={copy('win.ringOn')}>
                  <p className="mt-1.5 text-sm text-foreground/90">{assessment.ringOn}</p>
                </Block>
              )
            )}
          </section>

          {rows.length > 0 && (
            <section>
              <p className={LABEL}>{copy('ai.byArea')}</p>
              <ul className="divide-y divide-border">
                {rows.map(({ area, read }) => (
                  <li key={area} className="py-5">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-sm font-semibold text-foreground">{areaLabel(area)}</h3>
                      <VerdictPill verdict={read.verdict} />
                      <span className="text-xs text-muted-foreground">
                        {copy(`win.basis.${isBasis(read.basis) ? read.basis : 'unstated'}`)}
                      </span>
                    </div>
                    {read.kind && (
                      <p className="mt-2 text-base font-medium text-foreground">
                        {findingLabel(area, read.kind)}
                      </p>
                    )}
                    {read.note && (
                      <p className="mt-1.5 text-sm leading-relaxed text-foreground/80">
                        {read.note}
                      </p>
                    )}
                    {read.changesMindWhen && (
                      <p className="mt-3 text-sm leading-relaxed text-foreground/80">
                        <span className="font-medium text-foreground">
                          {copy('win.changeMind')}
                        </span>{' '}
                        {read.changesMindWhen}
                      </p>
                    )}
                    {read.sources.length > 0 ? (
                      <ul className="mt-3 space-y-2">
                        {read.sources.map((source) => (
                          <li key={source.claimId}>
                            <QuoteBlock>
                              <p>“{source.quote}”</p>
                              <div className="mt-2 flex flex-wrap items-center gap-2">
                                <SourceTag>
                                  {`${source.sourceLabel} · ${formatShortDate(source.date)}`}
                                </SourceTag>
                                <button
                                  type="button"
                                  onClick={() => onViewSource?.(source.claimId)}
                                  className="text-xs text-muted-foreground transition-colors hover:text-foreground"
                                >
                                  {copy('win.viewSource')}
                                </button>
                              </div>
                            </QuoteBlock>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="mt-3 text-xs text-muted-foreground">{copy('win.noSource')}</p>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {others.length > 0 && (
            <section>
              <p className={LABEL}>{copy('win.otherAreas')}</p>
              <ul className="divide-y divide-border">
                {others.map(({ area, text }) => (
                  <li key={area} className="py-5">
                    <h3 className="text-sm font-semibold text-foreground">{areaLabel(area)}</h3>
                    {text && (
                      <p className="mt-2 text-sm leading-relaxed text-foreground/90">{text}</p>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {assessment.outsideRecord.length > 0 && (
            <section>
              <p className={LABEL}>{copy('win.outside')}</p>
              <ul className="mt-2 list-disc space-y-2 pl-5">
                {assessment.outsideRecord.map((line) => (
                  <li key={line} className="text-sm leading-relaxed text-foreground/90">
                    {line}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {assessment.notes && (
            <section>
              <p className={LABEL}>{copy('win.notes')}</p>
              <p className="mt-2 border-l-2 border-border pl-3 text-sm leading-relaxed text-foreground/90">
                {assessment.notes}
              </p>
            </section>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
